'use client';

import { FunctionComponent, useState, useEffect } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { useTranslate } from '@xFrame4/components/Hooks';
import EmailOptInFormEntry from '@business/contact/EmailOptInFormEntry';
import EmailOptInForm from '@business/contact/EmailOptInForm';
import ALink from '@xFrame4/components/next/ALink';
import { useRouter } from 'next/navigation';
import AppTracker from '@components/tracking/AppTracker';

export interface EmailOptInConfirmationProps
{
    confirmationToken: string;
    language: string;
}

/**
 * Component: handles email opt-in confirmation with token processing
 */
const EmailOptInConfirmation: FunctionComponent<EmailOptInConfirmationProps> = (props) =>
{
    let t = useTranslate();
    let router = useRouter();

    const [isProcessing, setIsProcessing] = useState<boolean>(true);
    const [isConfirmed, setIsConfirmed] = useState<boolean>(false);
    const [isAlreadyConfirmed, setIsAlreadyConfirmed] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
    const [emailOptInForm, setEmailOptInForm] = useState<EmailOptInForm | null>(null);

    /** Process the confirmation token */
    const processConfirmation = async () =>
    {
        if (!props.confirmationToken)
        {
            setErrorMessage(t('EMAIL_OPT_IN_INVALID_TOKEN', props.language));
            setIsProcessing(false);

            return;
        }

        try
        {
            let result = await EmailOptInFormEntry.markAsConfirmed(props.confirmationToken);
            
            if (result.success)
            {
                setIsConfirmed(true);
                setRedirectUrl(result.redirectUrl);
                
                // Track the email opt-in form confirmation
                if (result.entry?.email)
                {
                    let phone = result.entry.phone && result.entry.phone != '' ? result.entry.phone : undefined;
                    // need to wait for the tracker to be loaded because processConfirmation is triggered right after the confirmation page is loaded
                    AppTracker.instance.on('loaded', () => {
                        AppTracker.trackEmailOptInFormConfirm(result.entry!.email, phone);
                    });
                }
                
                // Load the email opt-in form if we have a form ID
                if (result.formId)
                {
                    try
                    {
                        let formResult = await EmailOptInForm.manager.get({ id: result.formId });
                        if (formResult)
                        {
                            setEmailOptInForm(formResult);
                        }
                    }
                    catch (formError)
                    {
                        console.error('Error loading email opt-in form:', formError);
                        // Continue without the form - fallback to default message
                    }
                }
                
                // If there's a redirect URL, redirect after a short delay
                if (result.redirectUrl)
                {
                    setTimeout(() => {
                        router.push(result.redirectUrl!);
                    }, 8000); // 8 second delay to show success message
                }
            }
            else if (result.alreadyConfirmed)
            {
                // Handle already confirmed case
                setIsAlreadyConfirmed(true);
                setRedirectUrl(result.redirectUrl);
                
                // Load the email opt-in form if we have a form ID
                if (result.formId)
                {
                    try
                    {
                        let formResult = await EmailOptInForm.manager.get({ id: result.formId });
                        if (formResult)
                        {
                            setEmailOptInForm(formResult);
                        }
                    }
                    catch (formError)
                    {
                        console.error('Error loading email opt-in form:', formError);
                        // Continue without the form - fallback to default message
                    }
                }
                
                // If there's a redirect URL, redirect after a short delay
                if (result.redirectUrl)
                {
                    setTimeout(() => {
                        router.push(result.redirectUrl!);
                    }, 4000); // 4 second delay to show already confirmed message
                }
            }
            else
            {
                setErrorMessage(t('EMAIL_OPT_IN_CONFIRMATION_FAILED', props.language));
            }
        }
        catch (error)
        {
            console.error('Error confirming email opt-in:', error);
            setErrorMessage(t('EMAIL_OPT_IN_CONFIRMATION_ERROR', props.language));
        }
        finally
        {
            setIsProcessing(false);
        }
    };

    /** Process confirmation on mount */
    useEffect(() =>
    {
        processConfirmation();
    }, [props.confirmationToken]);

    /** Processing state component */
    let cmpProcessing = (
        <div className="text-center">
            <Spinner animation="border" role="status" className="mb-3 email-opt-in-spinner">
                <span className="visually-hidden">{t('LOADING', props.language)}</span>
            </Spinner>
            <h3 className="mb-3 fw-medium">{t('EMAIL_OPT_IN_PROCESSING_CONFIRMATION', props.language)}</h3>
            <p>{t('EMAIL_OPT_IN_PROCESSING_CONFIRMATION_DESCRIPTION', props.language)}</p>
        </div>
    );

    /** Success state redirect action */
    let cmpSuccessRedirect = (
        <div className="mt-4">
            <p className="text-muted">{t('EMAIL_OPT_IN_REDIRECTING', props.language)}</p>
            <Spinner animation="border" role="status" size="sm" className="me-2">
                <span className="visually-hidden">{t('LOADING', props.language)}</span>
            </Spinner>
        </div>
    );

    /** Success state homepage action */
    let cmpSuccessHomepage = (
        <div className="mt-4">
            <ALink href={`/${props.language}`} className="btn btn-primary btn-lg rounded-3 px-4 py-2 fw-semibold text-uppercase">
                {t('GO_TO_HOMEPAGE', props.language)}
            </ALink>
        </div>
    );

    /** Get the success message from form or fallback to default */
    const getSuccessMessage = () =>
    {
        if (emailOptInForm)
        {
            let translation = emailOptInForm.getTranslation(props.language);
            if (translation?.formConfirmationSuccessMessage)
            {
                return translation.formConfirmationSuccessMessage;
            }
        }

        return t('EMAIL_OPT_IN_CONFIRMATION_SUCCESS_DESCRIPTION', props.language);
    };

    /** Success state component */
    let cmpSuccess = (
        <div className="text-center">
            <div className="mb-4">
                <i className="fa fa-check-circle text-success email-opt-in-icon"></i>
            </div>
            <h2 className="text-success mb-3 fw-semibold">{t('EMAIL_OPT_IN_CONFIRMATION_SUCCESS', props.language)}</h2>
            <p className="lead mb-4 email-opt-in-lead">{getSuccessMessage()}</p>
            {redirectUrl ? cmpSuccessRedirect : cmpSuccessHomepage}
        </div>
    );

    /** Already confirmed state component */
    let cmpAlreadyConfirmed = (
        <div className="text-center">
            <div className="mb-4">
                <i className="fa fa-info-circle text-info email-opt-in-icon"></i>
            </div>
            <h2 className="text-info mb-3 fw-semibold">{t('EMAIL_OPT_IN_ALREADY_CONFIRMED', props.language)}</h2>
            <p className="lead mb-4 email-opt-in-lead">{t('EMAIL_OPT_IN_ALREADY_CONFIRMED_DESCRIPTION', props.language)}</p>
            {redirectUrl ? cmpSuccessRedirect : cmpSuccessHomepage}
        </div>
    );

    /** Error state component */
    let cmpError = (
        <div className="text-center">
            <div className="mb-4">
                <i className="fa fa-exclamation-triangle text-danger email-opt-in-icon"></i>
            </div>
            <h2 className="text-danger mb-3 fw-semibold">{t('EMAIL_OPT_IN_CONFIRMATION_FAILED', props.language)}</h2>
            <Alert variant="danger" className="mt-3">
                {errorMessage}
            </Alert>
            <div className="mt-4">
                <ALink href={`/${props.language}`} className="btn btn-primary rounded-3 px-4 py-2 fw-semibold text-uppercase">
                    {t('GO_TO_HOMEPAGE', props.language)}
                </ALink>
            </div>
        </div>
    );

    /** Render */
    return (
        <div className="email-opt-in-confirmation p-4 text-center">
            {isProcessing ? cmpProcessing : (isConfirmed ? cmpSuccess : (isAlreadyConfirmed ? cmpAlreadyConfirmed : cmpError))}
        </div>
    );
}

export default EmailOptInConfirmation;